<script setup lang="ts">
import BaseHeaderDropdownLocale from "./BaseHeaderDropdownLocale.vue";
import BaseHeaderAuthButtons from "./BaseHeaderAuthButtons.vue";
import BaseHeaderBurgerMenu from "./BaseHeaderBurgerMenu.vue";

export interface HeaderUser {
  fullName: string;
}

export interface BaseHeaderProps {
  /**
   * An array of the header links that placed next to the logo.
   */
  links?: IHeaderLink[];
  /**
   * Set transparent background for the header.
   */
  transparent?: boolean;
  /**
   * Sets header with fixed style.
   */
  fixed?: boolean;
  /**
   * Sets header with absolute style.
   */
  absolute?: boolean;
  /**
   * URL to the header logo. Height is fixed to 56px(full header height), width auto adjusted.
   */
  logoImageUrl?: string;
  /**
   * Shows search input.
   */
  withSearch?: boolean;
  searchValue?: string;
  /**
   * Shows "Sign In" and "Sign Up" buttons.
   */
  withAuth?: boolean;
  /**
   * Shows "Sign Out" / "Logout" buttons.
   *
   * Note: this is incompatible with the 'withAuth' prop.
   */
  withLocale?: boolean;
  /**
   * Shows dropdown button with locale list.
   */
  withUser?: null | HeaderUser;
  withMobileMenu?: boolean;
  withThemeSwitcher?: boolean;
  withNotifications?: boolean;
  defaultLocale?: ILocale;
  availableLocales: ILocale[];
}
const emit = defineEmits(["logout", "search", "clear"]);
const props = withDefaults(defineProps<BaseHeaderProps>(), {
  logoImageUrl: "/images/mock/logo.svg",
  withThemeSwitcher: true,
});

const links = computed(() => props.links?.filter(link => link.withUserOnly ? Boolean(props.withUser) : true));
const searchValue = ref(props.searchValue);
const onInputSubmit = () => emit("search", searchValue.value);
const clearSearch = () => (searchValue.value = "");
const isVisibleDropdownLocale = computed(() => props.availableLocales.length > 1);
watch(() => props.searchValue, q => (searchValue.value = q));
</script>

<template>
  <header class="baseHeader" :class="{ baseHeader_fixed: props.fixed, baseHeader_absolute: props.absolute, baseHeader_default: !props.transparent }">
    <div class="flex items-center max-w-[100%]">
      <BaseButton class="flex-shrink-0" type="icon" tag="nuxt-link" to="/" name="logo-link" link>
        <BaseImage class="baseHeader__logo" :src="props.logoImageUrl" fallback-src="/images/mock/logo.svg" alt="Logo" width="auto" :height="56" />
      </BaseButton>
      <BaseHeaderLinks v-if="links?.length" :links="links" />
    </div>
    <slot name="append-center" />
    <div class="flex items-center sm:gap-2 lg:gap-6">
      <div v-if="props.withSearch">
        <BaseSearchInput v-model="searchValue" class="!hidden lg:!flex" @keyup.enter="onInputSubmit()" @clear="clearSearch()" />
        <BaseButton class="flex items-center justify-center lg:hidden" type="icon" name="search" title="Search" @click="onInputSubmit()">
          <IconSearch class="mx-2 text-main" />
        </BaseButton>
      </div>
      <template v-if="props.withUser?.fullName">
        <BaseHeaderDropdownLocale
          v-if="isVisibleDropdownLocale"
          class="!hidden lg:!flex"
          :default-locale="props.defaultLocale"
          :available-locales="props.availableLocales"
        />
        <BaseButton v-if="props.withNotifications" name="notifications" title="Notifications" type="icon">
          <IconBell />
        </BaseButton>
        <BasePopover
          class="items-center justify-center hidden lg:flex"
          content-class="background-main shadow-main rounded-main min-w-[256px] !w-auto"
          placement="bottom-right"
        >
          <template #default="{ toggle, open: isMenuExpanded }">
            <BaseButton name="user-menu" type="icon" title="Menu" :aria-expanded="isMenuExpanded" @click="toggle">
              <IconLogoInitials :title="props.withUser.fullName" />
            </BaseButton>
          </template>
          <template #content="{ close }">
            <BaseHeaderUserLinksList @logout="emit('logout')" @close="close">
              <template v-if="props.withThemeSwitcher" #bottom>
                <li role="separator" class="hr" style="background: var(--base-header-menu-line-color)" />
                <li class="my-6 text-center">
                  <BaseThemeSwitcher has-labels />
                </li>
              </template>
            </BaseHeaderUserLinksList>
          </template>
        </BasePopover>
      </template>
      <div v-if="props.withAuth" class="items-center hidden lg:flex justify-end">
        <BaseHeaderDropdownLocale v-if="isVisibleDropdownLocale" :default-locale="props.defaultLocale" :available-locales="props.availableLocales" />
        <BaseHeaderAuthButtons />
      </div>
      <div v-if="props.withLocale" class="hidden sm:flex m-2">
        <BaseHeaderDropdownLocale v-if="isVisibleDropdownLocale" :default-locale="props.defaultLocale" :available-locales="props.availableLocales" />
      </div>
      <BaseHeaderBurgerMenu
        v-if="props.withMobileMenu"
        :links="links"
        :with-auth="props.withAuth"
        :with-user="props.withUser"
        :with-theme-switcher="props.withThemeSwitcher"
        @logout="emit('logout')"
      />
      <slot name="append-end" />
    </div>
  </header>
</template>

<style lang="scss">
//FIXME: style
.baseHeader {
  @apply w-full md:h-14 px-4 md:px-10 flex justify-between items-center z-[100] relative transition-all;
  border-bottom: 1px solid transparent;

  &.baseHeader_absolute {
    @apply absolute top-0 left-0;
  }

  &.baseHeader_fixed {
    @apply fixed top-0 left-0;
  }

  &.baseHeader_default {
    @apply py-4 md:py-[3.25rem];
    background-color: var(--base-background-color);
    border-bottom: 1px solid var(--base-header-line-color);
  }

  .baseHeader__links {
    @apply hidden lg:flex items-center overflow-hidden;

    .baseHeader__link {
      @apply mx-6 font-medium flex-shrink-0 line-clamp-2 max-w-[9rem] text-center hover:text-[var(--base-text-color)];

      text-decoration-line: none;
    }
  }

  .router-link-active {
    color: var(--base-text-color) !important;
  }

  .headerDropdown .headerDropdown__menu,
  .basePopover__content_bottom-right {
    @apply rtl:-right-[245px]; // TODO: Fix this in popover.
  }
}

.baseHeader__logo {
  @apply max-h-14 w-auto;
}
</style>
